<template>
  <div>
    <div
      v-if="showReceipts"
      class="receipts-view"
    >
      <div class="d-flex align-center mb-4">
        <v-btn
          text
          color="primary"
          @click="backToTable"
        >
          <v-icon left>
            {{ icons.mdiArrowLeft }}
          </v-icon>
          タグ一覧に戻る
        </v-btn>
      </div>
      <receipts-data
        :date="receiptsDate"
        :interval="propsInterval"
        :end-date="receiptsEndDate"
        :customer-tag-id="selectedCustomerTagIdForReceipts"
        @open-receipt="$emit('open-receipt', $event)"
        @revisit="$emit('revisit', $event)"
      />
    </div>
    <v-data-table
      v-else
      ref="dataTable"
      :loading="isLoading"
      :headers="headers"
      :items="filteredCustomerTagData"
      :header-props="{ sortByText: 'ソート' }"
      :footer-props="{ itemsPerPageText: '行/ページ:' }"
      no-data-text="データがありません"
      :page="$route.query.page ? Number($route.query.page) : 1"
      class="customer-tag-data-table"
      @update:page="($event) => {
        $vuetify.goTo($refs.dataTable)
        addQuery({ page: $event })
      }"
    >
      <template #loading>
        <v-skeleton-loader type="table" />
      </template>

      <template #top>
        <div class="w-full d-flex align-center">
          <v-menu
            v-model="isFilterMenuOpen"
            :close-on-content-click="false"
            min-width="300"
            offset-y
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                class="ml-2"
              >
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ icons.mdiFilterMenuOutline }}
                </v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-subheader>顧客タグ</v-subheader>
              <v-list-item>
                <v-list-item-content class="px-2">
                  <v-select
                    v-model="selectedCustomerTagId"
                    :items="customerTags"
                    item-text="attributes.name"
                    :item-value="item => Number(item.id)"
                    :prepend-inner-icon="icons.mdiPound"
                    :menu-props="{ auto: true, offsetY: true }"
                    clearable
                    hide-details
                    hide-selected
                    no-data-text="選択可能なデータがありません"
                    class="pa-0 ma-0"
                    @change="onCustomerTagSelected"
                    @click:clear="onCustomerTagCleared"
                  />
                </v-list-item-content>
              </v-list-item>

              <v-subheader>日付レンジ</v-subheader>
              <v-list-item>
                <v-list-item-content class="px-2">
                  <v-menu
                    v-model="isDateRangeMenuOpen"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    min-width="auto"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        :value="dateRangeDisplay"
                        :prepend-inner-icon="icons.mdiCalendar"
                        readonly
                        v-bind="attrs"
                        hide-details
                        v-on="on"
                      />
                    </template>
                    <v-date-picker
                      v-model="dateRange"
                      range
                      locale="ja"
                      :day-format="date => new Date(date).getDate()"
                      color="primary"
                      no-title
                      :max="maxEndDate"
                      :min="minDate"
                      @change="onDateRangeChange"
                    />
                  </v-menu>
                </v-list-item-content>
              </v-list-item>
              <v-subheader />
            </v-list>
          </v-menu>
          <v-spacer />
          <v-text-field
            v-model="tagNameFilter"
            :prepend-inner-icon="icons.mdiMagnify"
            placeholder="タグ名で絞り込み"
            hide-details
            dense
            clearable
            class="mr-2"
            style="max-width: 200px;"
          />
          <v-btn
            :loading="isLoading"
            :disabled="customerTagData.length <= 0 || isLoading"
            :ripple="false"
            color="secondary"
            small
            class="ma-2"
            @click="download"
          >
            <v-icon left>
              {{ icons.mdiFileExcel }}
            </v-icon>
            ダウンロード
          </v-btn>
        </div>
      </template>

      <template #[`item.receiptsLink`]="{ item }">
        <v-btn
          text
          small
          color="primary"
          @click="goToReceipts(item)"
        >
          伝票一覧
        </v-btn>
      </template>

      <template #[`item.attributes.subtotal`]="{ item }">
        {{ item.attributes.subtotal ? `¥${item.attributes.subtotal.toLocaleString()}` : '¥0' }}
      </template>

      <template #[`item.attributes.cashAmount`]="{ item }">
        {{ item.attributes.cashAmount ? `¥${item.attributes.cashAmount.toLocaleString()}` : '¥0' }}
      </template>

      <template #[`item.attributes.cardAmount`]="{ item }">
        {{ item.attributes.cardAmount ? `¥${item.attributes.cardAmount.toLocaleString()}` : '¥0' }}
      </template>

      <template #[`item.attributes.receivableAmount`]="{ item }">
        {{ item.attributes.receivableAmount ? `¥${item.attributes.receivableAmount.toLocaleString()}` : '¥0' }}
      </template>

      <template #[`item.attributes.totalPayment`]="{ item }">
        {{ item.attributes.totalPayment ? `¥${item.attributes.totalPayment.toLocaleString()}` : '¥0' }}
      </template>

      <template #[`footer.page-text`]="props">
        {{ props.pageStart }} - {{ props.pageStop }} / {{ props.itemsLength }}
      </template>
    </v-data-table>
  </div>
</template>

<script>
import {
  ref, computed, watch, getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiFileExcel, mdiFilterMenuOutline, mdiPound, mdiCalendar, mdiMagnify, mdiArrowLeft,
} from '@mdi/js'
import { format, addYears, parseISO } from 'date-fns'
import * as XLSX from 'xlsx/xlsx.mjs'
import { each, get } from 'lodash'
import useQueryString from '@/views/composable/useQueryString'
import useCompInit from '@/views/composable/useCompInit'
import CustomerTagSalesApi from '@/api/v2/statistics/CustomerTagSales'
import CustomerTagApi from '@/api/waiter/CustomerTag'
import ReceiptsData from '@/views/components/data/Receipts.vue'
import { CustomerTagDataHeaders } from '@/utils/constants/table-headers'

export default {
  components: {
    ReceiptsData,
  },
  props: {
    date: {
      type: String,
      required: true,
      default: () => new Date().toISOString().substr(0, 10),
    },
    interval: {
      type: Number,
      required: true,
      default: 0,
    },
    endDate: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const { route, addQuery } = useQueryString()
    const { isLoading, initWith } = useCompInit()
    const vm = getCurrentInstance().proxy
    const customerTagData = ref([])
    const isDownloading = ref(false)
    const customerTags = ref([])
    const selectedCustomerTagId = ref(null)
    const isFilterMenuOpen = ref(false)
    const isDateRangeMenuOpen = ref(false)
    const dateRange = ref([props.date, props.endDate || props.date])
    const tagNameFilter = ref('')
    const showReceipts = ref(false)
    const selectedCustomerTagIdForReceipts = ref(null)

    const headers = computed(() => [
      {
        text: '伝票リンク',
        value: 'receiptsLink',
        sortable: false,
      },
      ...CustomerTagDataHeaders,
    ])

    const getCustomerTags = async () => {
      const res = await CustomerTagApi.getCustomerTags()

      if (res?.status === 200) {
        customerTags.value = [...res.data.customerTags.data]
      }
    }

    const maxEndDate = computed(() => {
      if (!dateRange.value[0]) return null
      const startDate = parseISO(dateRange.value[0])
      const maxDate = addYears(startDate, 1)

      return format(maxDate, 'yyyy-MM-dd')
    })

    const minDate = computed(() => {
      // 必要に応じて最小日付を設定
      return null
    })

    const dateRangeDisplay = computed(() => {
      if (!dateRange.value[0]) return '日付を選択'
      if (dateRange.value.length === 1 || !dateRange.value[1]) {
        return format(parseISO(dateRange.value[0]), 'yyyy/MM/dd')
      }
      const start = format(parseISO(dateRange.value[0]), 'yyyy/MM/dd')
      const end = format(parseISO(dateRange.value[1]), 'yyyy/MM/dd')

      return `${start} ~ ${end}`
    })

    const filteredCustomerTagData = computed(() => {
      if (!tagNameFilter.value) {
        return customerTagData.value
      }
      const filterText = tagNameFilter.value.toLowerCase()

      return customerTagData.value.filter(item => {
        const tagName = item.attributes?.customerTagName || ''

        return tagName.toLowerCase().includes(filterText)
      })
    })

    const getCustomerTagSales = async ({
      date = null,
      interval = null,
      endDate = null,
      customerTagId = null,
    }) => {
      const res = await CustomerTagSalesApi.getCustomerTagSales({
        date,
        interval,
        endDate,
        customerTagId,
      })

      if (res?.status === 200) {
        customerTagData.value = [...(res.data.data?.data || [])]
      }
    }

    const fetchData = () => {
      const params = {
        date: dateRange.value[0] || props.date,
        interval: props.interval,
        endDate: dateRange.value[1] && dateRange.value[1] !== dateRange.value[0] ? dateRange.value[1] : null,
        customerTagId: selectedCustomerTagId.value || null,
      }
      getCustomerTagSales(params)
    }

    const onCustomerTagSelected = () => {
      fetchData()
    }

    const onCustomerTagCleared = () => {
      selectedCustomerTagId.value = null
      fetchData()
    }

    const onDateRangeChange = dates => {
      if (!dates || dates.length === 0) return
      if (dates.length === 1) {
        dateRange.value = [dates[0], dates[0]]
      } else if (dates.length === 2) {
        // 開始日が終了日より後の場合は無効
        if (dates[0] > dates[1]) {
          dateRange.value = [dates[0], dates[0]]

          return
        }
        // 1年を超える場合は終了日を開始日から1年後に制限
        const startDate = parseISO(dates[0])
        const endDate = parseISO(dates[1])
        const maxDate = addYears(startDate, 1)
        if (endDate > maxDate) {
          dateRange.value = [dates[0], format(maxDate, 'yyyy-MM-dd')]
        } else {
          dateRange.value = dates
        }
      }
      isDateRangeMenuOpen.value = false
      fetchData()
    }

    const receiptsDate = computed(() => {
      return dateRange.value[0] || props.date
    })

    const receiptsEndDate = computed(() => {
      return dateRange.value[1] && dateRange.value[1] !== dateRange.value[0]
        ? dateRange.value[1]
        : ''
    })

    const goToReceipts = item => {
      selectedCustomerTagIdForReceipts.value = item.attributes?.customerTagId || null
      showReceipts.value = true
    }

    const backToTable = () => {
      showReceipts.value = false
      selectedCustomerTagIdForReceipts.value = null
    }

    const buildXlsxData = () => {
      return customerTagData.value.map(item => {
        const data = {}
        each(CustomerTagDataHeaders, h => {
          const value = get(item, h.value)
          if (h.value.includes('Amount') || h.value.includes('subtotal') || h.value.includes('totalPayment')) {
            data[h.text] = value || 0
          } else {
            data[h.text] = value
          }
        })

        return data
      })
    }

    const download = () => {
      isDownloading.value = true

      const excelData = buildXlsxData()

      const wb = XLSX.utils.book_new()
      const data = XLSX.utils.json_to_sheet(excelData)
      XLSX.utils.book_append_sheet(wb, data, 'タグ')

      let title = `${props.date}_${['日', '週', '月'][props.interval]}`
      if (props.endDate) title = `${props.date}~${props.endDate}`
      XLSX.writeFile(wb, `タグ_${title}.xlsx`)

      isDownloading.value = false
    }

    // propsの変更を監視してデータを再取得
    watch(() => [props.date, props.interval, props.endDate], () => {
      dateRange.value = [props.date, props.endDate || props.date]
      fetchData()
    })

    initWith([
      getCustomerTags(),
      fetchData(),
    ])

    return {
      // data
      customerTagData,
      filteredCustomerTagData,
      isLoading,
      headers,
      customerTags,
      selectedCustomerTagId,
      isFilterMenuOpen,
      isDateRangeMenuOpen,
      dateRange,
      dateRangeDisplay,
      maxEndDate,
      minDate,
      tagNameFilter,
      propsInterval: props.interval,

      // methods
      getCustomerTagSales,
      getCustomerTags,
      addQuery,
      download,
      onCustomerTagSelected,
      onCustomerTagCleared,
      onDateRangeChange,
      fetchData,
      goToReceipts,
      backToTable,
      showReceipts,
      receiptsDate,
      receiptsEndDate,
      selectedCustomerTagIdForReceipts,

      icons: {
        mdiFileExcel,
        mdiFilterMenuOutline,
        mdiPound,
        mdiCalendar,
        mdiMagnify,
        mdiArrowLeft,
      },
    }
  },
}
</script>

<style lang="scss">
.customer-tag-data-table {
  white-space: nowrap;
}
</style>
